import React, {FC} from 'react';

import {
  defineMessage,
  MessageDescriptor,
  useI18n,
} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {UnreachableError} from '../../../../../../libs/shared/core/src/helpers/types/customErrors';
import {buildTeammateDisplayName} from '../../../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {TeammateAvatar} from '../../../../../../libs/shared/ui/src/components/avatar/teammateAvatar';
import {InteractiveListItemPublicProps} from '../../../../../../libs/shared/ui/src/components/interactiveList/interactiveListItem';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {ModifiersEnum} from '../../../../../../srcCommon/inboxes/search/searchModifiersHelpers';
import {
  InboxesSearchDropdownSuggestion,
  SuggestionSelectHandler,
} from '../../../../../../srcCommon/inboxes/search/searchSuggestionsHelpers';
import {AccountModel} from '../../../../../models/api/accountModel';
import {AccountAvatar, computeAccountAvatarUrl} from '../../../../common/avatars/accountAvatar';
import {renderTagNameInTooltip} from '../../../../common/dropdowns/tagDropdownItem';
import {SearchSuggestionsDropdownDynamicObjectItemIcon} from './searchSuggestionsDropdownDynamicObjectItemIcon';
import {SearchSuggestionsDropdownItem} from './searchSuggestionsDropdownItem';
import {SearchSuggestionsDropdownTagItemIcon} from './searchSuggestionsDropdownTagItemIcon';
import {SearchSuggestionsDropdownTagItemLabel} from './searchSuggestionsDropdownTagItemLabel';

/*
 * Props.
 */

interface SearchSuggestionsDropdownSuggestionProps {
  suggestion: InboxesSearchDropdownSuggestion;
  onSuggestionSelect: SuggestionSelectHandler;
}

/*
 * Component.
 */

export const SearchSuggestionsDropdownSuggestion: FC<SearchSuggestionsDropdownSuggestionProps> = (props) => {
  const {suggestion, onSuggestionSelect} = props;
  const {value, onSelectOptions, modifier, fuzzyQuery, score, isSmartMixSuggestion} = suggestion;

  const {i18n} = useI18n();

  const onSelect: InteractiveListItemPublicProps['onSelect'] = (e, key) =>
    onSuggestionSelect(value, key, onSelectOptions);

  const humanReadableModifierDescriptor = getHumanReadableModifierDescriptor(modifier);
  const humanReadableModifier = humanReadableModifierDescriptor && i18n._(humanReadableModifierDescriptor);

  if ('valuePlaceholder' in suggestion) {
    // If we have a placeholder, it means the suggestion is a modifier one.
    return (
      <SearchSuggestionsDropdownItem
        onSelect={onSelect}
        prefix={humanReadableModifier}
        shouldBoldPrefix
        label={suggestion.valuePlaceholder}
        shouldItalicizeLabel
        score={score}
        fuzzyQuery={fuzzyQuery}
      />
    );
  }

  if ('account' in suggestion) {
    return (
      <SearchSuggestionsDropdownItem
        onSelect={onSelect}
        prefix={humanReadableModifier}
        shouldBoldPrefix={!isSmartMixSuggestion}
        icon={maybeRenderAccountIcon(suggestion.account)}
        label={suggestion.account.name}
        score={score}
        fuzzyQuery={fuzzyQuery}
      />
    );
  }

  if ('teammate' in suggestion) {
    // Use the teammate's email if the modifier is `with`, otherwise use its alias.
    const suffix = modifier === ModifiersEnum.WITH ? suggestion.teammate.email : suggestion.teammate.alias;

    return (
      <SearchSuggestionsDropdownItem
        onSelect={onSelect}
        prefix={humanReadableModifier}
        shouldBoldPrefix={!isSmartMixSuggestion}
        icon={<TeammateAvatar size={VisualSizesEnum.SMALL} teammate={suggestion.teammate} />}
        label={buildTeammateDisplayName(suggestion.teammate)}
        suffix={`(${suffix})`}
        score={score}
        fuzzyQuery={fuzzyQuery}
      />
    );
  }

  if ('contactMatch' in suggestion) {
    const {
      contactMatch: {name, handle},
    } = suggestion;

    return (
      <SearchSuggestionsDropdownItem
        onSelect={onSelect}
        prefix={humanReadableModifier}
        shouldBoldPrefix={!isSmartMixSuggestion}
        label={name}
        suffix={name !== handle && `(${handle})`}
        score={score}
        fuzzyQuery={fuzzyQuery}
      />
    );
  }

  if ('dynamicObjectType' in suggestion) {
    return (
      <SearchSuggestionsDropdownItem
        onSelect={onSelect}
        prefix={humanReadableModifier}
        icon={<SearchSuggestionsDropdownDynamicObjectItemIcon type={suggestion.dynamicObjectType} />}
        label={suggestion.label}
        score={score}
        fuzzyQuery={fuzzyQuery}
      />
    );
  }

  if ('tag' in suggestion) {
    return (
      <SearchSuggestionsDropdownItem
        onSelect={onSelect}
        prefix={humanReadableModifier}
        shouldBoldPrefix={!isSmartMixSuggestion}
        icon={<SearchSuggestionsDropdownTagItemIcon tag={suggestion.tag} />}
        label={
          <SearchSuggestionsDropdownTagItemLabel
            fuzzyQuery={fuzzyQuery}
            tag={suggestion.tag}
            tagsPath={suggestion.tagsPath}
            shouldShowDeduplication={suggestion.shouldShowDeduplication}
          />
        }
        renderLabelTooltip={() => renderTagNameInTooltip(suggestion.tag, suggestion.tagsPath)}
        score={score}
        fuzzyQuery={fuzzyQuery}
      />
    );
  }

  if ('label' in suggestion) {
    return (
      <SearchSuggestionsDropdownItem
        onSelect={onSelect}
        prefix={humanReadableModifier}
        shouldBoldPrefix={!isSmartMixSuggestion}
        label={suggestion.label}
        score={score}
        fuzzyQuery={fuzzyQuery}
      />
    );
  }

  const [, modifierValue] = suggestion.value.split(':');
  const humanReadableValueDescriptor = getHumanReadableValueDescription(modifierValue);
  const humanReadableValue = humanReadableValueDescriptor
    ? i18n._(humanReadableValueDescriptor)
    : modifierValue;

  return (
    <SearchSuggestionsDropdownItem
      onSelect={onSelect}
      prefix={humanReadableModifier}
      shouldBoldPrefix={!isSmartMixSuggestion}
      label={humanReadableValue}
      score={score}
      fuzzyQuery={fuzzyQuery}
    />
  );
};

/*
 * Helpers.
 */

function getHumanReadableModifierDescriptor(modifier: ModifiersEnum): MessageDescriptor | null {
  switch (modifier) {
    case ModifiersEnum.ACCOUNT:
      return defineMessage({
        id: 'search-suggestion-prefix-account',
        message: 'account',
      });

    case ModifiersEnum.AFTER:
      return defineMessage({
        id: 'search-suggestion-prefix-after',
        message: 'after',
      });

    case ModifiersEnum.ASSIGNEE:
      return defineMessage({
        id: 'search-suggestion-prefix-assignee',
        message: 'assignee',
      });

    case ModifiersEnum.AUTHOR:
      return defineMessage({
        id: 'search-suggestion-prefix-author',
        message: 'author',
      });

    case ModifiersEnum.BCC:
      return defineMessage({
        id: 'search-suggestion-prefix-bcc',
        message: 'bcc',
      });

    case ModifiersEnum.BEFORE:
      return defineMessage({
        id: 'search-suggestion-prefix-before',
        message: 'before',
      });

    case ModifiersEnum.BODY:
      return defineMessage({
        id: 'search-suggestion-prefix-body',
        message: 'body includes',
      });

    case ModifiersEnum.CC:
      return defineMessage({
        id: 'search-suggestion-prefix-cc',
        message: 'cc',
      });

    case ModifiersEnum.CHANNEL_TYPE:
      return defineMessage({
        id: 'search-suggestion-prefix-channel_type',
        message: 'channel type',
      });

    case ModifiersEnum.COMMENTER:
      return defineMessage({
        id: 'search-suggestion-prefix-commenter',
        message: 'commenter',
      });

    case ModifiersEnum.CUSTOM_FIELD:
      return null;

    case ModifiersEnum.DURING:
      return defineMessage({
        id: 'search-suggestion-prefix-during',
        message: 'during',
      });

    case ModifiersEnum.FROM:
      return defineMessage({
        id: 'search-suggestion-prefix-from',
        message: 'from',
      });

    case ModifiersEnum.HAS:
      return defineMessage({
        id: 'search-suggestion-prefix-has',
        message: 'has',
      });

    case ModifiersEnum.ID:
      return null;

    case ModifiersEnum.IN:
      return defineMessage({
        id: 'search-suggestion-prefix-in',
        message: 'in',
      });

    case ModifiersEnum.INBOX:
      return defineMessage({
        id: 'search-suggestion-prefix-inbox',
        message: 'inbox',
      });

    case ModifiersEnum.IS:
      return defineMessage({
        id: 'search-suggestion-prefix-is',
        message: 'is',
      });

    case ModifiersEnum.LINK:
    case ModifiersEnum.OBJECT:
      return defineMessage({
        id: 'search-suggestion-prefix-object',
        message: 'object',
      });

    case ModifiersEnum.MENTION:
      return defineMessage({
        id: 'search-suggestion-prefix-mention',
        message: 'mention',
      });

    case ModifiersEnum.PARTICIPANT:
      return defineMessage({
        id: 'search-suggestion-prefix-participant',
        message: 'participant',
      });

    case ModifiersEnum.RECIPIENT:
      return defineMessage({
        id: 'search-suggestion-prefix-recipient',
        message: 'recipient',
      });

    case ModifiersEnum.SUBJECT:
      return defineMessage({
        id: 'search-suggestion-prefix-subject',
        message: 'subject includes',
      });

    case ModifiersEnum.TAG:
      return defineMessage({
        id: 'search-suggestion-prefix-tag',
        message: 'tag',
      });

    case ModifiersEnum.TO:
      return defineMessage({
        id: 'search-suggestion-prefix-to',
        message: 'to',
      });

    case ModifiersEnum.TYPE:
      return defineMessage({
        id: 'search-suggestion-prefix-type',
        message: 'type',
      });

    case ModifiersEnum.WITH:
      return defineMessage({
        id: 'search-suggestion-prefix-with',
        message: 'with',
      });

    default:
      throw new UnreachableError(modifier);
  }
}

function getHumanReadableValueDescription(value: string): MessageDescriptor | null {
  switch (value) {
    // `has` properties.
    case 'no-tags':
      return defineMessage({
        id: 'search-suggestion-value-no-tags',
        message: 'no tags',
      });

    // `is` properties.
    case 'shared-with-me':
      return defineMessage({
        id: 'search-suggestion-value-shared-with-me',
        message: 'shared with me',
      });

    // `type` properties.
    case 'googleplay':
      return defineMessage({
        id: 'search-suggestion-value-google-play',
        message: 'google play',
      });

    case 'phone-call':
      return defineMessage({
        id: 'search-suggestion-value-phone-call',
        message: 'phone call',
      });

    case 'tweet_dm':
      return defineMessage({
        id: 'search-suggestion-value-tweet-dm',
        message: 'tweet dm',
      });

    case 'yalo_wha':
      return defineMessage({
        id: 'search-suggestion-value-yalo-wha',
        message: 'yalo',
      });

    case 'front_chat':
      return defineMessage({
        id: 'search-suggestion-value-front-chat',
        message: 'front chat',
      });

    default:
      return null;
  }
}

function maybeRenderAccountIcon(account: AccountModel) {
  const accountAvatarUrl = computeAccountAvatarUrl(account);

  if (!accountAvatarUrl) {
    return null;
  }

  return <AccountAvatar account={account} size={16} avatarIconSize={16} avatarUrl={accountAvatarUrl} />;
}
